import { ChangeDetectorRef, Component, OnInit, ViewChild, ChangeDetectionStrategy, CUSTOM_ELEMENTS_SCHEMA, inject } from '@angular/core';
import { IonInput, ModalController, Platform, IonButton, IonContent, IonFooter, IonIcon, IonItem, IonLabel, IonToggle, IonToolbar } from '@ionic/angular';
import { ErrorReportConfirmPage } from '../error-report-confirm/error-report-confirm.page';
import { AFSTextInputComponent, AFSPasswordInputComponent } from '@afs-ng/inputs';
import { environment } from '../../../environments/environment';
import { LoginCredentials, AuthService } from '../../services/auth.service';
import { DarkmodeService } from '../../services/darkmode.service';
import { FeedbackService } from '../../services/feedback.service';
import { GlobalSettingsService } from '../../services/global-settings.service';
import { HttpService, HttpFunctionResult } from '../../services/http.service';
import { OfflineModeService } from '../../services/offline-mode.service';
import { StorageManagerService } from '../../services/storage-manager.service';
import { ZeroconfService } from '../../services/zeroconf.service';
import { FormsModule } from '@angular/forms';
import { AfsButtonComponent } from '@afs-ng/buttons';
import { Swiper } from 'swiper/types';
import { AFSToastLoadingService, AFSConfirmService } from '@afs-ng/overlays';


@Component({
    selector: 'app-setup',
    templateUrl: './setup.page.html',
    styleUrls: ['./setup.page.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, AFSTextInputComponent, AfsButtonComponent, AFSPasswordInputComponent, IonButton, IonContent, IonFooter, IonIcon, IonItem, IonLabel, IonToggle, IonToolbar]
})      
export class SetupPage implements OnInit  {
  private feedback = inject(FeedbackService);
  private confirmSrv = inject(AFSConfirmService);
  private http = inject(HttpService);
  private storageManager = inject(StorageManagerService);
  private authSrv = inject(AuthService);
  platform = inject(Platform);
  private settings = inject(GlobalSettingsService);
  private changeDetector = inject(ChangeDetectorRef);
  private darkmode = inject(DarkmodeService);
  private modalCtrl = inject(ModalController);
  private offlineModeSrv = inject(OfflineModeService);
  zeroconfSrv = inject(ZeroconfService);
  private toastLoading = inject(AFSToastLoadingService);


  public serverAddress: string = ""
  public loginCredentials: LoginCredentials = {
     username:  "",
     password:  ""    
  }

  public appSettings: {errorReport: boolean, offlineMode: boolean, darkmode: boolean} = {
    errorReport: true,
    offlineMode: true,
    darkmode: true
  }

  public isDemo: boolean = environment.isDemo

  // @ViewChild(IonSlides, {static: false}) slides: IonSlides
  @ViewChild('swiper') swiperRef: any;
  @ViewChild("usernameInput", {static: false}) usernameInput: AFSTextInputComponent;
  swiper?: Swiper;

  public currentSlide: number = 1

  public allowSwipe: boolean = false

  public slideOpts = {  
    initialSlide: 0,
    locked: true,
    speed: 600
  };

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.platform.ready().then(async ()=>{
      this.serverAddress = (await this.storageManager.getItem<string>("BASEURL", ""))
        .replace("http://", "")
        .replace("https://", "")
      if(!this.platform.is("cordova") && this.serverAddress == "") this.serverAddress = window.location.origin 
    }) 
    this.appSettings.darkmode = this.darkmode.isDarkModeValue
  } 
  ngOnInit(): void {
    this.zeroconfSrv.subscribeZeroConf()
  } 

  public async focusNextInput(nextInput: IonInput) {
    await nextInput.setFocus();
    this.changeDetector.detectChanges();
  } 

   

  private connectUrl: string = ""
  public async continue(){
    if(this.currentSlide == 1) await this.saveServer()
    else if(this.currentSlide == 2) await this.login()
    else await this.complete()
  }

  private async saveServer(){
      //http wegschneiden damit zuerst mit https getestet werden kann
      if(this.serverAddress.startsWith("http://")) this.serverAddress = this.serverAddress.replace("http://", "")
      if(this.serverAddress.startsWith("https://")) this.serverAddress = this.serverAddress.replace("https://", "")

      //Zuerst https, dann http testen. Erste erreichbare URL wird verwendet
      const connectUrl: string | null = await this.toastLoading.showLoading({title: "Teste Verbindung...", delay: 0, minDuration: 300}, async () => {
        for (const protocol of ["https", "http"]) {
          const url = `${protocol}://${this.serverAddress}`
          this.http.defaultURL = url
          const result: HttpFunctionResult<any> = await this.http.GET({
            path: "",
            timeout: 5000,
            handleError: false
          })
          if(result.success) return url
        }
        return null
      })

      if(!connectUrl){
        await this.feedback.showError("Keine Verbindung möglich. Bitte versuchen sie eine andere Server-Adresse.")
        return
      }

      this.connectUrl = connectUrl
      await this.storageManager.setItem<string>("BASEURL", this.connectUrl)
      if(this.connectUrl.startsWith("http://")) {
        if(!(await this.confirmSrv.confirm({message: 'Die Verbindung zu diesem Server ist unsicher, da sie nicht über "https" erreichbar ist. Möchten Sie trotzdem fortfahren?', acceptButton: {label: "Ja, vertrauen", role: "confirm"}, cancelButton: {label: "Abbrechen"}}))){
          await this.storageManager.delete("BASEURL")
          return
        }
      }
      this.zeroconfSrv.unsubscribeZeroConf()
      await this.next()
      setTimeout(async () => await this.usernameInput.focus(), 500); 
  }

  public async login(){
    const result: HttpFunctionResult<any> = await this.toastLoading.showLoading(
      {title: "Anmelden...", delay: 0, minDuration: 300},
      () => this.authSrv.login(this.loginCredentials)
    )
    if(this.isDemo) await this.storageManager.setItem<string>("BASEURL", this.connectUrl)
    if(result.success) await this.next()
  }

  public async complete(){

    //Offline-Mode
    await this.storageManager.setItem<boolean>("OFFLINE_MODE_ACTIVE", this.appSettings.offlineMode)
    this.offlineModeSrv.active = this.appSettings.offlineMode

    //Sentry
    await this.settings.setEntryEnabled(this.appSettings.errorReport)

    await this.modalCtrl.dismiss()
  }

  public async next(){ 
    if(this.isDemo) return
    this.allowSwipe = true
    this.changeDetector.detectChanges()
    this.swiperRef?.nativeElement?.swiper.slideNext()
    this.allowSwipe = false
    this.currentSlide++
  }

  public async serverKeyDown(event: KeyboardEvent){
    if(event.key == "Tab") event.preventDefault()
    if(event.key == "Enter") await this.saveServer()
  }

  public async startDemo(){
    this.connectUrl = environment.testserver
    this.http.defaultURL = environment.testserver
    await this.login() 

  }

  public async serverClicked(server: {name?: string, domain?: string }){
    this.serverAddress = server.domain
    await this.saveServer()
  }  

  public async scanQRCode(){
    const qrResult: HttpFunctionResult<string> = await this.feedback.getQRCodeData()
    if(!qrResult.data) return
    this.serverAddress = qrResult.data 
    await this.saveServer() 
  }

  public async showErrorReportText(){
    await this.feedback.openModal(ErrorReportConfirmPage)
  }

  public async toggleDarkmode(){
    this.settings.saveDarkmode(this.appSettings.darkmode)
  }

}
